import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/subpage.css";
import "../../Styles/pages/staffing-firms.css";

export default function StaffingFirms() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const cleanups = [];
    const observers = [];
    const $ = (sel) => root.querySelector(sel);
    const $$ = (sel) => Array.prototype.slice.call(root.querySelectorAll(sel));

    /* ── number count-up ── */
    function countUp(el, dur = 900) {
      const raw = (el.dataset.count || el.textContent).trim();
      const m = raw.match(/^([^\d]*)([\d.,]+)([^\d]*)$/);
      if (!m) { el.textContent = raw; return; }
      const prefix = m[1];
      const suffix = m[3];
      const hadComma = m[2].includes(",");
      const target = parseFloat(m[2].replace(/,/g, ""));
      if (reduceMotion || isNaN(target)) { el.textContent = raw; return; }
      const t0 = performance.now();
      (function tick(now) {
        const p = Math.min(1, (now - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        const n = target * eased;
        let s = Number.isInteger(target) ? Math.round(n).toString() : n.toFixed(0);
        if (hadComma) s = Math.round(n).toLocaleString("en-IN");
        el.textContent = prefix + s + suffix;
        if (p < 1) requestAnimationFrame(tick);
        else el.textContent = raw;
      })(t0);
    }
    function countPanel(panel) {
      if (!panel) return;
      panel.querySelectorAll(".sf-counter .v, .sf-says .extras strong").forEach((el, i) => {
        if (!el.dataset.count) el.dataset.count = el.textContent.trim();
        setTimeout(() => countUp(el), i * 70);
      });
    }

    /* ── firm-type switcher tabs ── */
    const tabs = $$(".sf-tab");
    const panels = $$(".sf-panel");
    tabs.forEach((btn) => {
      const handler = () => {
        const tab = btn.dataset.tab;
        tabs.forEach((b) => b.setAttribute("aria-selected", b.dataset.tab === tab));
        panels.forEach((p) => {
          const on = p.dataset.tab === tab;
          p.classList.toggle("active", on);
          if (on) countPanel(p);
        });
      };
      btn.addEventListener("click", handler);
      cleanups.push(() => btn.removeEventListener("click", handler));
    });

    /* ── intersection-driven reveals ── */
    const crCard = $("#crCard");
    if ("IntersectionObserver" in window) {
      const eyeOb = new IntersectionObserver(
        (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("lit"); }),
        { rootMargin: "0px 0px -20% 0px" }
      );
      $$(".eye").forEach((el) => eyeOb.observe(el));
      observers.push(eyeOb);

      if (crCard) {
        const dialOb = new IntersectionObserver(
          (entries, ob) => {
            entries.forEach((e) => {
              if (!e.isIntersecting) return;
              crCard.classList.add("built");
              const num = crCard.querySelector(".dial .num");
              if (num) {
                num.textContent = "0";
                setTimeout(() => countUp(num, 1200), 120);
              }
              ob.unobserve(crCard);
            });
          },
          { threshold: 0.4 }
        );
        dialOb.observe(crCard);
        observers.push(dialOb);
      }
    } else {
      $$(".eye").forEach((el) => el.classList.add("lit"));
      if (crCard) crCard.classList.add("built");
    }

    /* ── evidence-row source reveal ── */
    if (crCard) {
      const ev = crCard.querySelector(".left");
      if (ev) {
        const handler = (e) => {
          const li = e.target.closest("li");
          if (li) li.classList.toggle("src-open");
        };
        ev.addEventListener("click", handler);
        cleanups.push(() => ev.removeEventListener("click", handler));
      }
    }

    /* ── default panel count after load ── */
    const t1 = setTimeout(() => countPanel($(".sf-panel.active")), 350);
    cleanups.push(() => clearTimeout(t1));

    return () => {
      cleanups.forEach((fn) => fn());
      observers.forEach((ob) => ob.disconnect());
    };
  }, []);

  return (
    <div className="page ann-subpage" ref={rootRef}>
      {/* HERO */}
      <section className="sf-hero" id="top">

        <span className="eye-strong">For staffing &amp; recruitment firms</span>

        <h1>More mandates. <em>Faster fills.</em> Without adding headcount<span className="period" /></h1>

        <div className="sub-row">
          <p className="lede-l">
            Ann is the hiring intelligence behind your desk — <b>sourcing the candidates others miss, reading every résumé, and handing your team an evidence-backed shortlist your client can't argue with.</b> Contingency, RPO, or retained.
          </p>
          <div className="ctas">
            <a href="#cta" className="btn btn-primary btn-lg">Book a demo</a>
            <a href="#firms" className="btn btn-secondary btn-lg">See it by firm type</a>
          </div>
        </div>
      </section>

      {/* OUTCOME BAND */}
      <div className="sf-outcome">
        <div className="grid">
          <div className="lbl">The result</div>
          <p className="text">
            More placements per recruiter, shorter time-to-fill, and shortlists clients <em>say yes to</em> — the three numbers your P&amp;L actually runs on<b>.</b>
          </p>
        </div>
      </div>

      {/* ECONOMICS */}
      <section className="sf-econ" id="econ">
        <div className="head">
          <span className="eye">Why it pays</span>
          <h2>Three numbers that decide a <em>desk</em>.</h2>
          <p className="lede">You're paid for placements — and for keeping the clients who sent them. Ann moves all three numbers underneath that.</p>
        </div>

        <div className="grid">
          <div className="item">
            <div className="num">01 · Capacity</div>
            <h3>Each recruiter runs <em>more searches at once</em>.</h3>
            <p><b>The reading, sourcing, and screening stop being the ceiling.</b> Ann does the heavy lift across every open role, so a recruiter's time goes to clients and candidates — the parts that actually close.</p>
            <div className="metric">
              <div className="v">3×</div>
              <div className="k">roles in play<br />per recruiter</div>
            </div>
          </div>
          <div className="item">
            <div className="num">02 · Velocity</div>
            <h3>Time-to-fill is <em>revenue velocity</em>.</h3>
            <p><b>The faster a role fills, the sooner it bills — and the less likely a client gives it to three other agencies too.</b> Ann compresses brief-to-shortlist from weeks to days.</p>
            <div className="metric">
              <div className="v">9 days</div>
              <div className="k">median brief<br />to shortlist</div>
            </div>
          </div>
          <div className="item">
            <div className="num">03 · Win rate</div>
            <h3>Win the mandate. <em>Keep the client.</em></h3>
            <p><b>A shortlist that arrives with its evidence converts to interviews — and to placements.</b> Clients stop second-guessing your CVs and start trusting your desk. Trust is what renews the contract.</p>
            <div className="metric">
              <div className="v">+34%</div>
              <div className="k">CV-to-interview<br />conversion</div>
            </div>
          </div>
        </div>
        <p className="disclaimer">// Figures illustrative of design-partner pilots · your numbers depend on desk, sector, and mandate mix.</p>
      </section>

      {/* FIRM-TYPE SWITCHER */}
      <section className="sf-switch" id="firms">
        <div className="head">
          <span className="eye">Every kind of firm</span>
          <h2>Contingency, RPO, or retained — <em>same intelligence</em>.</h2>
          <p className="lede">Ann isn't built for one business model. Pick the way your firm bills and works, and see the gear Ann runs in.</p>
        </div>

        <div className="sf-tabs" role="tablist">
          <button className="sf-tab" role="tab" aria-selected="true" data-tab="cont">
            <span className="num">01 · Contingency &amp; Perm</span>
            <div className="ttl">Fill <em>more, faster</em>.</div>
            <div className="sub">High-volume desks racing other agencies to the placement.</div>
          </button>
          <button className="sf-tab" role="tab" aria-selected="false" data-tab="rpo">
            <span className="num">02 · RPO &amp; MSP</span>
            <div className="ttl">Hold every <em>SLA</em>.</div>
            <div className="sub">Embedded teams running a client's hiring at scale.</div>
          </button>
          <button className="sf-tab" role="tab" aria-selected="false" data-tab="exec">
            <span className="num">03 · Executive search</span>
            <div className="ttl">Map the <em>whole market</em>.</div>
            <div className="sub">Retained boutiques where the long list has to be exhaustive.</div>
          </button>
        </div>

        <div className="sf-panels">
          {/* Contingency */}
          <div className="sf-panel active" data-tab="cont" role="tabpanel">
            <div className="sf-says">
              <div className="sig"><span className="g" /><span className="for">Contingency &amp; Perm</span></div>
              <p className="speech">A client floats a role to four agencies at once. Ann reads the market overnight and surfaces a <em>ready-to-send</em> shortlist by morning — so your CVs land <b>first</b>, and land <b>better</b>.</p>
              <div className="extras">
                <div className="item"><strong>1,840</strong>Read this role</div>
                <div className="item"><strong>6</strong>Sent to client by 9am</div>
              </div>
            </div>
            <div className="sf-vis">
              <div className="topbar"><div className="dots"><span /><span /><span /></div><span>Sales Exec · client: NorthBridge</span><span className="live">Ann live</span></div>
              <div className="body">
                <div className="sf-counter">
                  <div className="cell"><div className="v">1,840</div><div className="k">Read</div></div>
                  <div className="cell"><div className="v">71</div><div className="k">Qualified</div></div>
                  <div className="cell featured"><div className="v">6</div><div className="k">Client-ready</div></div>
                </div>
                <div className="sf-row"><div className="av" style={{ background: "var(--src-quick)" }}>RK</div><div className="nm">Ravi Kapoor <span>· 4y field sales</span></div><span className="scr h">90</span></div>
                <div className="sf-row"><div className="av" style={{ background: "var(--src-jobpost)" }}>SM</div><div className="nm">Sneha Iyer <span>· SaaS AE · 3y</span></div><span className="scr h">87</span></div>
                <div className="sf-row"><div className="av" style={{ background: "var(--src-quick)" }}>AT</div><div className="nm">Arun T. <span>· inside sales</span></div><span className="scr m">79</span></div>
                <div className="sf-more">+ 3 more, ready to send</div>
              </div>
            </div>
          </div>

          {/* RPO */}
          <div className="sf-panel" data-tab="rpo" role="tabpanel">
            <div className="sf-says">
              <div className="sig"><span className="g" /><span className="for">RPO &amp; MSP</span></div>
              <p className="speech">Embedded in a client running 40 reqs at once? Ann holds the same bar across <em>every</em> one — so SLAs hold without you throwing more recruiters at the contract, and every hire is <b>defensible</b> to the client's own audit.</p>
              <div className="extras">
                <div className="item"><strong>40</strong>Live reqs held</div>
                <div className="item"><strong>96%</strong>SLA adherence</div>
              </div>
            </div>
            <div className="sf-vis">
              <div className="topbar"><div className="dots"><span /><span /><span /></div><span>RPO · client: Meridian Group</span><span className="live">Ann live</span></div>
              <div className="body">
                <div className="sf-counter">
                  <div className="cell"><div className="v">40</div><div className="k">Live reqs</div></div>
                  <div className="cell"><div className="v">11</div><div className="k">Recruiters</div></div>
                  <div className="cell featured"><div className="v">96%</div><div className="k">SLA held</div></div>
                </div>
                <div className="sf-row"><div className="av" style={{ background: "var(--src-talent)" }}>OPS</div><div className="nm">Ops Analyst <span>· 8 reqs</span></div><span className="tag">on track</span></div>
                <div className="sf-row"><div className="av" style={{ background: "var(--src-jobpost)" }}>CS</div><div className="nm">CS Tier-2 <span>· 14 reqs</span></div><span className="tag">on track</span></div>
                <div className="sf-row"><div className="av" style={{ background: "var(--src-upload)" }}>FIN</div><div className="nm">Finance <span>· 5 reqs</span></div><span className="tag">at risk · 1</span></div>
                <div className="sf-more">consistent bar · every req</div>
              </div>
            </div>
          </div>

          {/* Exec search */}
          <div className="sf-panel" data-tab="exec" role="tabpanel">
            <div className="sf-says">
              <div className="sig"><span className="g" /><span className="for">Executive search</span></div>
              <p className="speech">On a retained search, the longlist has to be the <em>whole market</em> — and the client is paying to know you left no one out. Ann maps every credible candidate, surfaces the under-the-radar ones, and gives you a dossier worth the retainer.</p>
              <div className="extras">
                <div className="item"><strong>214</strong>Market mapped</div>
                <div className="item"><strong>9</strong>Longlist presented</div>
              </div>
            </div>
            <div className="sf-vis">
              <div className="topbar"><div className="dots"><span /><span /><span /></div><span>Retained · VP Engineering · confidential</span><span className="live">Ann live</span></div>
              <div className="body">
                <div className="sf-counter">
                  <div className="cell"><div className="v">214</div><div className="k">Mapped</div></div>
                  <div className="cell"><div className="v">31</div><div className="k">Pre-vetted</div></div>
                  <div className="cell featured"><div className="v">9</div><div className="k">Longlist</div></div>
                </div>
                <div className="sf-row"><div className="av" style={{ background: "var(--src-talent)" }}>KN</div><div className="nm">Kavya Nair <span>· VP Eng · scale-up</span></div><span className="scr h">93</span></div>
                <div className="sf-row"><div className="av" style={{ background: "var(--src-talent)" }}>DS</div><div className="nm">Deepak S. <span>· Sr Director → VP</span></div><span className="scr h">89</span></div>
                <div className="sf-row"><div className="av" style={{ background: "var(--src-upload)" }}>MF</div><div className="nm">Maria F. <span>· under-titled · ready</span></div><span className="scr h">86</span></div>
                <div className="sf-more">+ 6 more · full audit trail</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MULTI-CLIENT WORKSPACE */}
      <section className="sf-multi" id="board">
        <div className="head">
          <span className="eye">One desk, many clients</span>
          <h2>Every mandate, <em>one workspace</em>.</h2>
          <p className="lede">No more "which spreadsheet is that client in?" Every mandate sits side by side — each client's pipeline separate, each shortlist client-ready, the whole desk at a glance.</p>
        </div>

        <div className="sf-board">
          <div className="bar">
            <span className="l">Your desk · 4 active mandates</span>
            <span className="r">11 client-ready · 2 awaiting your review</span>
          </div>
          <div className="sf-clients">
            <div className="sf-client">
              <div className="ch"><span className="badge" style={{ background: "var(--src-quick)" }}>N</span><span className="cn">NorthBridge</span></div>
              <div className="role">Sales Exec · ×3</div>
              <div className="mini"><div className="av" style={{ background: "var(--src-quick)" }}>RK</div><span className="nm">Ravi Kapoor</span><span className="scr">90</span></div>
              <div className="mini"><div className="av" style={{ background: "var(--src-jobpost)" }}>SI</div><span className="nm">Sneha Iyer</span><span className="scr">87</span></div>
              <div className="status">6 ready to send</div>
            </div>
            <div className="sf-client">
              <div className="ch"><span className="badge" style={{ background: "var(--src-talent)" }}>M</span><span className="cn">Meridian</span></div>
              <div className="role">RPO · 40 reqs</div>
              <div className="mini"><div className="av" style={{ background: "var(--src-talent)" }}>OP</div><span className="nm">Ops Analyst</span><span className="scr">84</span></div>
              <div className="mini"><div className="av" style={{ background: "var(--src-jobpost)" }}>CS</div><span className="nm">CS Tier-2</span><span className="scr">81</span></div>
              <div className="status">96% SLA held</div>
            </div>
            <div className="sf-client">
              <div className="ch"><span className="badge" style={{ background: "var(--src-upload)" }}>C</span><span className="cn">Confidential</span></div>
              <div className="role">Retained · VP Eng</div>
              <div className="mini"><div className="av" style={{ background: "var(--src-talent)" }}>KN</div><span className="nm">Kavya Nair</span><span className="scr">93</span></div>
              <div className="mini"><div className="av" style={{ background: "var(--src-talent)" }}>DS</div><span className="nm">Deepak S.</span><span className="scr">89</span></div>
              <div className="status">longlist ready</div>
            </div>
            <div className="sf-client">
              <div className="ch"><span className="badge" style={{ background: "var(--src-jobpost)" }}>A</span><span className="cn">Aster Health</span></div>
              <div className="role">Nurses · ×12</div>
              <div className="mini"><div className="av" style={{ background: "var(--src-jobpost)" }}>PJ</div><span className="nm">Priya Joshi</span><span className="scr">91</span></div>
              <div className="mini"><div className="av" style={{ background: "var(--src-quick)" }}>VK</div><span className="nm">Vidya K.</span><span className="scr">88</span></div>
              <div className="status">2 awaiting review</div>
            </div>
          </div>
        </div>
        <p className="sf-multi note">// Each client's data stays walled off in its own workspace. <em>One desk sees all of theirs; no client ever sees another's.</em></p>
      </section>

      {/* CLIENT-READY ARTIFACT */}
      <section className="sf-artifact" id="artifact">
        <div className="head">
          <span className="eye">The deliverable</span>
          <h2>The shortlist that <em>closes the client</em>.</h2>
          <p className="lede">Every candidate comes with the case already made — a verdict, the evidence, the gaps. <b>Forward it to your client as-is. They read why before they even take your call.</b></p>
        </div>

        <div className="cr-card" id="crCard">
          <div className="barbar">
            <span className="l">Client-ready shortlist · candidate 1 of 6</span>
            <span className="r">Prepared for NorthBridge · shareable link</span>
          </div>
          <div className="body">
            <div className="left">
              <div className="who">NorthBridge · Sales Executive · contingency</div>
              <h3>Ravi Kapoor</h3>
              <p className="role">4y field sales · Pune · QuickSearch sourced</p>
              <div className="verdict">
                <div className="sig"><span className="g" /><span className="nm">Ann's read</span></div>
                <p className="text"><b>Strong fit — present with confidence.</b> Exceeds the client's revenue and territory criteria; one area to frame proactively when you pitch him.</p>
              </div>
              <div className="ev-section">
                <div className="lbl"><span>The case for Ravi</span><span style={{ color: "oklch(0.40 0.10 155)" }}>4 strong signals</span></div>
                <ul>
                  <li><span className="d" /><span className="ev-body"><span><b>127% of quota</b> across the last three years · consistent, not a single spike.</span><span className="src">verified · 2 manager references</span></span></li>
                  <li><span className="d" /><span className="ev-body"><span><b>Owns the exact territory</b> the client is hiring for · warm relationships already in place.</span><span className="src">CV + LinkedIn cross-check</span></span></li>
                  <li><span className="d" /><span className="ev-body"><span><b>Closed two logos the client wants</b> · direct, nameable, relevant wins.</span><span className="src">candidate interview, recorded</span></span></li>
                  <li><span className="d" /><span className="ev-body"><span><b>On the market quietly</b> · not in three other agencies' pipelines yet.</span><span className="src">sourcing recency check</span></span></li>
                </ul>
              </div>
              <div className="ev-section">
                <div className="lbl"><span>Frame this proactively</span><span style={{ color: "oklch(0.55 0.13 65)" }}>1 note</span></div>
                <ul>
                  <li className="gap"><span className="d" /><span className="ev-body"><span><b>Short tenure at current role (14mo).</b> Reason checks out — acquisition, team dissolved. Worth saying up front.</span><span className="src">candidate interview · reference confirmed</span></span></li>
                </ul>
              </div>
            </div>
            <div className="side">
              <div className="dial" data-build><div className="inner"><span className="num">88</span><span className="lbl">Fit score</span></div></div>
              <div className="meta-grid">
                <div className="cell"><div className="k">Client must-haves</div><div className="v">5 / 5</div></div>
                <div className="cell"><div className="k">References</div><div className="v">2 ✓</div></div>
                <div className="cell"><div className="k">Notes to frame</div><div className="v">1</div></div>
                <div className="cell"><div className="k">Slate rank</div><div className="v">#1/6</div></div>
              </div>
              <div className="actions">
                <a href="#" className="btn btn-primary btn-sm">Send to client</a>
                <a href="#" className="btn btn-secondary btn-sm">Add to longlist</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BUILT FOR */}
      <section className="sf-bf" id="built">
        <div className="head">
          <span className="eye">Across your firm</span>
          <h2>Good for the desk. Good for the <em>P&amp;L</em>.</h2>
        </div>
        <div className="grid">
          <div className="card">
            <div className="role">For owners &amp; directors</div>
            <h3>More billings <em>without</em> more headcount.</h3>
            <p>The lever every agency owner wants: grow placements faster than payroll. Ann lifts each recruiter's capacity, so margin expands as you scale — not the other way around.</p>
            <div className="foot">Capacity · margin · retention</div>
          </div>
          <div className="card">
            <div className="role">For delivery &amp; team leads</div>
            <h3>One bar, held across <em>every desk</em>.</h3>
            <p>No more quality that swings with whoever ran the search. Ann keeps the standard consistent across recruiters, mandates, and clients — and shows you where a desk is slipping before the client notices.</p>
            <div className="foot">Consistency · SLA · visibility</div>
          </div>
          <div className="card">
            <div className="role">For recruiters</div>
            <h3>Spend your day <em>closing</em>, not reading.</h3>
            <p>Ann does the sourcing, reading, and first screen. Your hours go to candidates and clients — the work that bills, the work you're actually good at, the reason you got into the desk.</p>
            <div className="foot">Less admin · more placements</div>
          </div>
        </div>
      </section>

      {/* PROOF */}
      <section className="sf-proof">
        <span className="eye">In early use with agency partners</span>
        <p className="quote">"We took on <span className="cy">two new clients</span> without hiring. Ann does the reading; my consultants do the closing. The CVs we send now <span className="cy">come back as interviews</span>."</p>
        <p className="attrib"><span className="nm">Managing Director</span> · perm &amp; contingency agency · 14 recruiters</p>
        <div className="stats">
          <div className="cell"><div className="v">3×</div><div className="k">roles in play<br />per recruiter</div></div>
          <div className="cell"><div className="v">9 days</div><div className="k">median brief<br />to shortlist</div></div>
          <div className="cell"><div className="v">+34%</div><div className="k">CV-to-interview<br />conversion</div></div>
        </div>
      </section>

      {/* FAQ */}
      <section className="sf-faq" id="faq">
        <div className="head">
          <span className="eye">FAQ</span>
          <h2>What agencies ask <em>first</em>.</h2>
          <p className="lede">Short answers. The commercial ones are best in a live conversation.</p>
        </div>
        <div className="items">
          <details className="faq-item" open>
            <summary><span className="num">01</span><span className="q">Does each client's data stay separate?</span><span className="ico">+</span></summary>
            <p className="a">Yes. Every client mandate runs in its own walled workspace — encrypted, access-controlled. Your desk sees all of yours; no client ever sees another's data. Candidate pools are never shared or trained on across clients.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">02</span><span className="q">Does it fit contingency, RPO, and retained?</span><span className="ico">+</span></summary>
            <p className="a">All three. Contingency desks run Ann at speed and volume; RPO teams use it to hold a consistent bar across many reqs; retained boutiques use it to map a whole market and build a dossier worth the fee. Same intelligence, different gear — run several models in one workspace.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">03</span><span className="q">Will it work with Bullhorn / our CRM?</span><span className="ico">+</span></summary>
            <p className="a">Bullhorn, JobAdder, Vincere, and Greenhouse are in beta, with two-way sync on candidates, stages, and notes. If you run something else, talk to us — we ship a new integration roughly every six weeks.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">04</span><span className="q">Can we share the shortlist with our clients?</span><span className="ico">+</span></summary>
            <p className="a">That's the point. Every candidate comes as a client-ready report — verdict, evidence, the notes worth framing — on a shareable link or as a forwarded document. Many agencies tell us it's become their differentiator in the pitch.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">05</span><span className="q">Does Ann replace our recruiters?</span><span className="ico">+</span></summary>
            <p className="a">No. Ann replaces the parts of the desk that aren't selling — the reading, the sourcing, the first screen. The client relationship, the negotiation, the close stay with your consultants. That's the work that bills, and the work Ann makes more time for.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">06</span><span className="q">How do you price for agencies?</span><span className="ico">+</span></summary>
            <p className="a">By desk and volume, not per placement — so the economics improve as you scale. We'll size it to your firm in the demo. If your model is unusual, we're flexible; bring it to the conversation.</p>
          </details>
        </div>
      </section>

      {/* CTA */}
      <section className="sf-cta" id="cta">
        <div className="grid">
          <div>
            <span className="eye-strong">See it on your desk</span>
            <h2>Bring a <b>live mandate</b>. See the <em>shortlist</em> Ann builds.</h2>
            <p className="lede-l">Fifteen minutes. <em>A real role off your desk</em> — contingency, RPO, or retained. Ann reads the market and shows you the client-ready shortlist it would hand back. You judge whether it beats what you'd send today.</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.currentTarget.querySelector("button").textContent = "Thanks — we'll be in touch.";
              }}
            >
              <input type="email" placeholder="you@youragency.com" required />
              <button type="submit" className="btn btn-primary btn-lg">Book the 15 minutes</button>
            </form>
            <p className="micro">No credit card · your data stays yours · DPA on request</p>
          </div>
          <div className="offer-card">
            <div className="ttl">What to bring</div>
            <div className="li"><span className="n">01</span><p className="txt"><b>One live mandate.</b> Whichever model you run — your call.</p></div>
            <div className="li"><span className="n">02</span><p className="txt"><b>The client brief.</b> Or just what the client told you on the phone. <em>Either works.</em></p></div>
            <div className="li"><span className="n">03</span><p className="txt"><b>Fifteen minutes.</b> A live look at the search and a client-ready candidate or two.</p></div>
          </div>
        </div>
      </section>
    </div>
  );
}
